import FilterMenu from "./filtermenu.js";
import SelectedFilters from "./selectedfilters.js";
import FilterValues from "./filtervalues.js";
import PriceRangeFilter from "./pricerangefilter.js";
import UpdateFilterUrl from "./updatefilterurl.js";
import SubSearchFilter from "./subsearchfilter.js";
import ProductQuantityFilter from "./productquantityfilter.js";
import { prodListValues } from './shared.js';

let currentUrl;

// set values from RouteData and QueryString
class UpdatePageFromUrl {
    /**
     * Updates the page based on the current URL by setting the appropriate filters and parameters.
     * @return {void}
     */
    update() {
        FilterMenu.unSelectAllFilters(false);

        currentUrl = window.location.href;

        const urlFilters = [
            { pattern: prodListValues.colorPattern, getFilter: () => FilterValues.getColorFilter(), getElementId: FilterValues.getColorElementId },
            { pattern: prodListValues.ratingPattern, getFilter: () => FilterValues.getRatingFilter(), getElementId: FilterValues.getRatingElementId },
            { pattern: prodListValues.prodTimePattern, getFilter: () => FilterValues.getProdTimeFilter(), getElementId: FilterValues.getProductionTimeElementId },
            { pattern: prodListValues.imprintLocationCountPattern, getFilter: () => FilterValues.getImprintLocationCountFilter(), getElementId: FilterValues.getImprintLocationCountElementId },
            { pattern: prodListValues.imprintColorCountPattern, getFilter: () => FilterValues.getImprintColorCountFilter(), getElementId: FilterValues.getImprintColorCountElementId },
        ];

        let filterCount = urlFilters.reduce((count, config) => count + applyUrlFilter(config.pattern, config.getFilter, config.getElementId), 0);
        filterCount += this.setTagFilter() + this.setpriceRangeFilter() + this.setSubSearch() + this.setProductQuantity();

        this.setPageNumber();
        this.setSortOrder();
        this.setPageSize();

        // If there are no filters applied make sure that the clear all filters option is not visible.
        if (filterCount === 0) {
            SelectedFilters.removeAllFromSelectedFilter();
        }
    };

    /**
     * Sets the price range filter based on the current URL.
     * @returns {number} The number of price range filters applied.
     */
    setpriceRangeFilter() {
        let matches = prodListValues.pricePattern.exec(currentUrl);
        let amounts = [];

        if (matches) {
            let vals = matches[0].split('/');
            if (vals.length >= 3) {
                amounts = amounts.concat(vals[2].split('-'));

                if (isNaN(parseFloat(amounts[0])) || isNaN(parseFloat(amounts[1]))) {
                    return 0;
                }

                let priceMin = (amounts[0] / 100);
                let priceMax = (amounts[1] / 100);

                PriceRangeFilter.update(priceMin, priceMax);
            }
        }

        return FilterValues.getPriceFilter().length;
    };

    /**
     * Sets the color filter based on the current URL.
     * @returns {number} The number of color filters applied.
     */
    setColorFilter() {
        return applyUrlFilter(prodListValues.colorPattern, () => FilterValues.getColorFilter(), FilterValues.getColorElementId);
    };

    /**
     * Sets the rating filter based on the current URL.
     * @returns {number} The number of rating filters applied.
     */
    setRatingFilter() {
        return applyUrlFilter(prodListValues.ratingPattern, () => FilterValues.getRatingFilter(), FilterValues.getRatingElementId);
    };

    /**
     * Sets the production time filter based on the current URL.
     * @returns {number} The number of production time filters applied.
     */
    setProductionTimeFilter() {
        return applyUrlFilter(prodListValues.prodTimePattern, () => FilterValues.getProdTimeFilter(), FilterValues.getProductionTimeElementId);
    };

    /**
     * Sets the imprint location count filter based on the current URL.
     * @returns {number} The number of imprint location count filters applied.
     */
    setImprintLocationCountFilter() {
        return applyUrlFilter(prodListValues.imprintLocationCountPattern, () => FilterValues.getImprintLocationCountFilter(), FilterValues.getImprintLocationCountElementId);
    };

    /**
     * Sets the imprint color count filter based on the current URL.
     * @returns {number} The number of imprint color count filters applied.
     */
    setImprintColorCountFilter() {
        return applyUrlFilter(prodListValues.imprintColorCountPattern, () => FilterValues.getImprintColorCountFilter(), FilterValues.getImprintColorCountElementId);
    };

    /**
     * Sets the page number based on the current URL.
     * @return {void}
     */
    setPageNumber() {
        /* set page number from url */
        let matches = prodListValues.pagePattern.exec(currentUrl);
        if (matches !== null) {
            let vals = matches[0].split('=');

            if (vals.length >= 2) {
                FilterValues.addPageNumber(vals[1]);
            }
        }
    };

    /**
     * Sets the page size based on the current URL.
     * @return {void}
     */
    setPageSize() {
        /* set page number from url */
        const matches = prodListValues.itemsPerPagePattern.exec(currentUrl);
        let page = FilterValues.getDefaultPageSize();

        if (matches !== null) {
            let vals = matches[0].split('=');

            if (vals.length >= 2) {
                page = vals[1];
            }
        } else {
            const element = document.getElementById('itemsPerPageDropDown');

            if (element && parseInt(element.value) !== page) {
                page = parseInt(element.value);
                UpdateFilterUrl.updatePageSize(page);
            }
        }

        FilterMenu.setPageSize(page);
    };

    /**
     * Sets the sub-search filter based on the current URL.
     * @returns {number} The number of sub-search filters applied.
     */
    setSubSearch() {
        let matches = prodListValues.subSearchPattern.exec(currentUrl);
        SubSearchFilter.clear();
        if (matches !== null) {
            let vals = matches[0].split('=');

            if (vals.length >= 2) {
                let term = unescape(vals[1]);
                SubSearchFilter.update(term);
                return 1;
            }
        }

        return 0;
    };

    /**
     * Sets the product quantity filter based on the current URL.
     * @returns {number} The number of product quantity filters applied.
     */
    setProductQuantity() {
        let matches = prodListValues.productQuantityPattern.exec(currentUrl);
        ProductQuantityFilter.clear();
        if (matches !== null) {
            let vals = matches[0].split('/');

            if (vals.length >= 2) {
                let term = unescape(vals[2]);
                ProductQuantityFilter.update(term);
                return 1;
            }
        }

        return 0;
    };

    /**
     * Sets the sort order based on the current URL.
     * @return {void}
     */
    setSortOrder() {
        /* set filter order from url*/
        const matches = prodListValues.sortByPattern.exec(currentUrl);
        let sort = FilterValues.getDefaultSort();

        if (matches) {
            for (let i = 0; i < prodListValues.sortMap.length; i++) {
                if (prodListValues.sortMap[i].q.toLowerCase() === matches[0].toLowerCase()) {
                    sort = prodListValues.sortMap[i].v.replace('string:', '');
                    break;
                }
            }
        }

        FilterMenu.setFilterOrder(FilterValues.getSortElementId(sort), false);
    };

    /**
     * Sets the tag filter based on the current URL.
     * @returns {number} The number of tag filters applied.
     */
    setTagFilter() {
        /* set tag filters from url */
        let matches = prodListValues.tagPattern.exec(currentUrl);
        let ids = [];
        let startLength = FilterValues.getTagFilter().length;
        let defaultTag = this.checkCustomUrl();

        if (matches) {
            let vals = matches[0].split('/');

            if (vals.length >= 3) {
                ids = ids.concat(vals[2].split('-'));
            }
        }

        if (defaultTag.length) {
            ids = ids.concat(defaultTag.split('-'));
        }

        if (ids.length) {
            FilterMenu.addTagFilters();
        }

        FilterMenu.markSelectedReload(FilterValues.getTagFilter(), ids, FilterValues.getTagElementId);
        FilterMenu.markUnSelectedReload(FilterValues.getTagFilter(), ids, FilterValues.getTagElementId, startLength);

        return FilterValues.getTagFilter().length;
    };

    /**
     * Checks the current URL for any custom filter rewrites and returns the default tag if found.
     * @return {string} The default tag from the URL if present, otherwise an empty string.
     */
    checkCustomUrl() {
        /* set values for rewriten urls (i.e. 'athletic-tshirts' is 'tag/97/t/4531' */
        let defaultTag = document.getElementById('rewriteDefaultTag').value;
        let urlVals = currentUrl.split('/');
        let urlVals2 = urlVals[3].split('?');
        if (document.getElementById('filterRewrite').value === '') {
            if (defaultTag !== '' && currentUrl.indexOf(document.getElementById('FilterFriendlyUrl').value) === -1) {
                document.getElementById('filterRewrite').value = urlVals2[0];
            } else {
                defaultTag = '';
                document.getElementById('rewriteDefaultTag').value = '';
            }
        } else if (urlVals2[0] !== document.getElementById('filterRewrite').value) {
            defaultTag = '';
        }

        return defaultTag;
    };
}

/**
 * Applies a standard URL filter by parsing IDs from the URL and marking filters as selected or unselected.
 * Handles the common pattern shared by color, rating, production time, and imprint count filters.
 * @param {RegExp} urlPattern - The regex pattern to match against the current URL.
 * @param {Function} getFilter - Returns the current filter array.
 * @param {Function} getElementId - Returns the DOM element ID for a filter entry.
 * @returns {number} The number of active filters after applying.
 */
function applyUrlFilter(urlPattern, getFilter, getElementId) {
    let matches = urlPattern.exec(currentUrl);
    let ids = [];
    let startLength = getFilter().length;

    if (matches) {
        let vals = matches[0].split('/');

        if (vals.length >= 3) {
            ids = ids.concat(vals[2].split('-'));
            FilterMenu.markSelectedReload(getFilter(), ids, getElementId);
        }
    }

    FilterMenu.markUnSelectedReload(getFilter(), ids, getElementId, startLength);

    return getFilter().length;
};

export default new UpdatePageFromUrl();